import * as moment from "moment";
import { IParamAction } from "../../Common/actionGenerics";
import { Actions as ActionTypes } from "./actionTypes";
import {
    BookingType,
    IAvailabilityStepInfo,
    IConfirmationStepInfo,
    IServiceStepInfo,
    IStore,
    Step,
} from "../DataTypes/storeTypes";
import { IConfirmData } from "../DataTypes/confirmationTypes";
import { IBookRequest } from "../DataTypes/requestTypes";
import { ClientPortalPaymentModes } from "../../Common/organisationInfo";
import { resetBooking, updateIsBusy } from "./actions";
import { post, FetchUtility, get, CustomError } from "../../Common/fetch";
import { error } from "../../Common/Notifications/helper";
import {
    ICreateSCAModel,
    IConfirmPaymentResponse,
    ISetupSCAModel,
    ICardInfo,
    ICreateSCABookModel,
} from "../../Common/Stripe/types";
import { GuidProvider } from "common.ui/Helpers/guidProvider";
import { selectIsStripeConnected, selectPortalPaymentMode } from "../Helpers/selectors";
import { Stripe, StripeElements, TokenResult } from "@stripe/stripe-js";
import { CardNumberElement } from "@stripe/react-stripe-js";

export function addNotes(showNotes: boolean): IParamAction<ActionTypes, boolean> {
    return { type: ActionTypes.UpdateShowNotes, parameter: showNotes };
}

export function showStripe(showStripe: boolean): IParamAction<ActionTypes, boolean> {
    return { type: ActionTypes.UpdateShowStripe, parameter: showStripe };
}

function updateBookedAppointment(appointment: any): IParamAction<ActionTypes, any> {
    return { type: ActionTypes.UpdateBookedAppointment, parameter: appointment };
}

export function setIdempotencyKeyAction(key: string): IParamAction<ActionTypes, string> {
    return { type: ActionTypes.SetIdempotencyKey, parameter: key };
}

export function setIdempotencyKey() {
    return (dispatch, getState: () => IStore) => {
        const state = getState();

        const info = state.stepsInfo[Step.Confirmation] as IConfirmationStepInfo;

        if (info.idempotencyKey) {
            return;
        }

        const key = GuidProvider.getGuid();

        dispatch(setIdempotencyKeyAction(key));
    };
}

export function submit(values: IConfirmData, stripe: Stripe, elements: StripeElements) {
    return (dispatch, getState: () => IStore) => {
        let state = getState();
        const cardElement = elements.getElement(CardNumberElement);

        dispatch(updateIsBusy(true));

        const info = state.stepsInfo[Step.Confirmation] as IConfirmationStepInfo;
        const serviceInfo = state.stepsInfo[Step.Service] as IServiceStepInfo;
        const amount = serviceInfo.amount ? serviceInfo.amount : 0;

        if (
            info.clientId == state.common.loggedInClientId &&
            !state.common.clients.some((x) => x.id == state.common.loggedInClientId)
        ) {
            return ShowError(dispatch, "Please select a client");
        }

        const paymentMode = selectPortalPaymentMode(state);
        const hasStripePayment =
            (paymentMode === ClientPortalPaymentModes.Required ||
                (paymentMode === ClientPortalPaymentModes.Optional && info.showStripe) ||
                info.isEditCreditCard ||
                serviceInfo.isPortalDeposit) &&
            amount > 0 &&
            selectIsStripeConnected(state);

        const isOffline = !info.isEditCreditCard && !!info.cardInfo;

        const locationInfo = state.stepsInfo[Step.Location];
        const providerInfo = state.stepsInfo[Step.Provider];
        const whenInfo = state.stepsInfo[Step.When] as IAvailabilityStepInfo;

        const date = whenInfo.slot.date;
        const clientLogs =
            "paymentMode: " +
            paymentMode +
            ", " +
            "ClientPortalPaymentModes.Required: " +
            ClientPortalPaymentModes.Required +
            ", " +
            "ClientPortalPaymentModes.Optional: " +
            ClientPortalPaymentModes.Optional +
            ", " +
            "info.showStripe: " +
            info.showStripe +
            ", " +
            "info.isEditCreditCard: " +
            info.isEditCreditCard +
            ", " +
            "serviceInfo.isPortalDeposit: " +
            serviceInfo.isPortalDeposit +
            ", " +
            "amount: " +
            amount +
            ", " +
            "selectIsStripeConnected: " +
            selectIsStripeConnected(state) +
            ", " +
            "hasStripePayment: " +
            hasStripePayment;

        const shouldCreateStripeToken = hasStripePayment && !isOffline;

        const bookRequestForValidation: IBookRequest = {
            clientId: info.clientId,
            locationId: locationInfo.id,
            serviceId: serviceInfo.id,
            providerId: providerInfo.id,
            date: date,
            offset: moment(date.toISOString()).utcOffset(),
            durationMinutes: whenInfo.slot.duration,
            notes: values.notes,
            hasTotalAmount: !serviceInfo.isPortalDeposit,
            appointmentId: whenInfo.slot.appointmentId,
            type: BookingType[state.initialSettings.type],
            idempotencyKey: info.idempotencyKey,
            clientLogs: clientLogs,
        };

        const validateBookingPromise: Promise<void> = hasStripePayment
            ? post<void>(state.initialSettings.validateBookingUrl, bookRequestForValidation)
            : Promise.resolve();

        const createPromise = validateBookingPromise.then(() => {
            return shouldCreateStripeToken ? stripe.createToken(cardElement) : undefined;
        });

        const stripePromise = createPromise.then((value: TokenResult | undefined) => {
            if (!hasStripePayment) {
                return "";
            }

            const result = value;
            if (result?.error) {
                throw new CustomError(result.error.message, result.error);
            }

            const model: ICreateSCABookModel = {
                amount: amount,
                serviceId: serviceInfo.id,
                saveDetails: values.storecard,
                isOffline: isOffline,
                idempotencyKey: info.idempotencyKey,
                practitionerId: providerInfo.id,
            };

            return post<IConfirmPaymentResponse>(state.initialSettings.createSCAIntent, model).then(async (result) => {
                if (isOffline) {
                    if (result.requiresAction) {
                        const handleActionResult = await stripe.handleNextAction({ clientSecret: result.clientSecret });
                        if (handleActionResult.error) {
                            // Reset so the next attempt gets a fresh PaymentIntent — the current one
                            // is in requires_payment_method after failed 3DS and cannot be reused.
                            dispatch(setIdempotencyKeyAction(GuidProvider.getGuid()));
                            throw new CustomError(handleActionResult.error.message, handleActionResult.error);
                        }
                    }

                    if (!values.storecard && state.initialSettings.revokeCardConsent) {
                        await post<void>(state.initialSettings.revokeCardConsent, {
                            practitionerId: providerInfo.id,
                        });
                    }

                    return result.paymentIntentId;
                }

                return stripe
                    .confirmCardPayment(result.clientSecret, {
                        payment_method: {
                            card: cardElement,
                        },
                    })
                    .then(function (result) {
                        if (result.error) {
                            throw new CustomError(result.error.message, result.error);
                        } else {
                            const model: ISetupSCAModel = {
                                paymentMethodId: result.paymentIntent.payment_method,
                                saveDetails: values.storecard,
                                practitionerId: providerInfo.id,
                            };

                            if (!values.storecard) {
                                return result.paymentIntent.id;
                            }

                            return post<void>(state.initialSettings.createOrUpdateCustomer, model)
                                .catch((error_) => {
                                    return ShowError(dispatch, error_.message);
                                })
                                .then(() => result.paymentIntent.id);
                        }
                    });
            });
        });

        const final = stripePromise.then((paymentIntentId: string) => {
            const request: IBookRequest = {
                clientId: info.clientId,
                locationId: locationInfo.id,
                serviceId: serviceInfo.id,
                providerId: providerInfo.id,
                date: date,
                offset: moment(date.toISOString()).utcOffset(),
                durationMinutes: whenInfo.slot.duration,
                notes: values.notes,
                paymentIntentId: paymentIntentId,
                hasTotalAmount: !serviceInfo.isPortalDeposit,
                appointmentId: whenInfo.slot.appointmentId,
                type: BookingType[state.initialSettings.type],
                idempotencyKey: info.idempotencyKey,
                clientLogs: clientLogs,
            };

            return post<any>(state.initialSettings.bookUrl, request).then((data) => {
                dispatch(updateBookedAppointment(data));
            });
        });

        final.catch((error_) => {
            let message = error_.message;
            if (message == FetchUtility.TIME_OUT) {
                message =
                    "We did not receive a response from our server. Please navigate to “My Appointments” to confirm if your booking was successful.";
            }

            dispatch(setIdempotencyKeyAction(GuidProvider.getGuid()));
            ShowError(dispatch, message);

            if (error_.data?.resetBooking) {
                dispatch(resetBooking());
            }
        });
    };
}

function ShowError(dispatch: any, message: string) {
    error(message);
    dispatch(updateIsBusy(false));
}

function updateCreditCard(card: ICardInfo): IParamAction<ActionTypes, ICardInfo> {
    return { type: ActionTypes.UpdateCreditCard, parameter: card };
}

export function getCreditCard(dispatch, state: IStore) {
    const providerInfo = state.stepsInfo[Step.Provider];

    return get<ICardInfo>(`${state.initialSettings.getCreditCardUrl}?practitionerId=${providerInfo.id}`)
        .then((data) => dispatch(updateCreditCard(data)))
        .catch((error_) => error(error_.message));
}

export function editCreditCard(isEdit: boolean): IParamAction<ActionTypes, boolean> {
    return { type: ActionTypes.EditCreditCard, parameter: isEdit };
}
